import React, {useEffect, useRef, useState} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {ActivityIndicator, View} from 'react-native';
import {KeyboardAwareScrollView} from 'react-native-keyboard-aware-scroll-view';
import Toast from 'react-native-toast-message';
import {zodResolver} from '@hookform/resolvers/zod';

import config from '../../../../config';
import {useModalDimensions} from '../../../../hooks/useModalDimensions';
import {useWebScreenDimensions} from '../../../../hooks/useWebScreenDimensions';
import {useSavePaymentCard} from '../../../../queries/paymentCard';
import {ERROR_MESSAGE, SUCCESS_CHECK} from '../../../ToastBottom';
import Button from '../../../ui-controls/Button';
import CrossPlatformModal from '../../../ui-controls/CrossPlatformModal';
import TextInput, {TextInputRef} from '../../../ui-controls/form/TextInput';
import Text from '../../../ui-controls/Text';
import {Title} from '../../../ui-controls/text/Title';
import {useTillIntegrationKey} from '../hooks';
import {SupportedCardIcons} from '../PaymentMethodSelector/PaymentIcons';
import type {TillCardInputData, TillPaymentInstance, TillPaymentJs} from '../types';
import {TILL_INPUT_STYLE_DEFAULT} from '../types';

import CardTokeniserTillProps from './CardTokeniserTillProps';
import {
  CardTokeniserTillSchema,
  CardTokeniserTillValues,
  useCardTokeniserTillSchemaDefaultValues,
} from './CardTokeniserTillSchema.zod';
import styles from './styles';
import {filterNumericOnly, showToastIfTokeniserErrors} from './util';

const tillInputStyleWithFont = {
  ...TILL_INPUT_STYLE_DEFAULT,
  'font-family':
    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
};

// https://gateway.tillpayments.com/documentation/gateway#paymentjs-javascript-integration

const CardTokeniserTillModal = ({onCancel = () => null, isVisible}: CardTokeniserTillProps) => {
  const savePaymentCard = useSavePaymentCard();

  const {integrationKey, reset: resetIntegrationKey} = useTillIntegrationKey(isVisible);
  const defaultValues = useCardTokeniserTillSchemaDefaultValues();

  const [isActivityIndicatorVisible, setActivityIndicatorVisible] = useState(true);
  const [paymentJsScript, setPaymentJsScript] = useState<TillPaymentJs | null>(null);
  const {isWebDesktopScreen} = useWebScreenDimensions();
  const {containerStyle: modalContainerStyle} = useModalDimensions({
    size: 'medium',
    constrainHeight: true,
  });

  // Ref for month input to enable tab navigation from CVV iframe
  const monthInputRef = useRef<TextInputRef>(null);

  // React Hook Form setup
  // mode: 'onChange' validates on every keystroke for real-time feedback
  const {control, handleSubmit, setValue, trigger, reset, formState} =
    useForm<CardTokeniserTillValues>({
      defaultValues,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      resolver: zodResolver(CardTokeniserTillSchema) as any,
      mode: 'onChange',
    });

  // Attach the script to the document body
  useEffect(() => {
    let script: HTMLScriptElement | null = null;

    if (isVisible) {
      script = document.createElement('script');
      script.src = config.TILL_GATEWAY;
      script.async = true;
      script.setAttribute('data-main', 'payment-js');
      script.addEventListener('load', () => setPaymentJsScript(new PaymentJs()));
      document.body.appendChild(script);
    }

    return () => {
      if (script) {
        setPaymentJsScript(null);
        document.body.removeChild(script);
        // Till injects another script to handle the iFrames injected into the divs, clean those up.
        const tillVault = document.body.querySelector('[data-main="vault-js"]');
        if (tillVault) {
          document.body.removeChild(tillVault);
        }
      }
    };
  }, [isVisible]);

  // Initialize Till Payment.js when script and integration key are ready
  useEffect(() => {
    if (paymentJsScript && integrationKey && isVisible) {
      paymentJsScript.init(
        integrationKey,
        'number_div',
        'cvv_div',
        (payment: TillPaymentInstance) => {
          payment.setNumberStyle(tillInputStyleWithFont);
          payment.setCvvStyle(tillInputStyleWithFont);

          payment.numberOn('input', (data: TillCardInputData) => {
            setValue('number', data.validNumber ? 'valid' : null);
            trigger('number');
          });

          payment.numberOn('blur', () => {
            trigger('number');
          });

          // Tab from card number to CVV
          payment.numberOn('tab', () => {
            const cvvIframe = document.querySelector('#cvv_div iframe') as HTMLIFrameElement;
            cvvIframe?.focus();
          });

          payment.cvvOn('input', (data: TillCardInputData) => {
            setValue('cvv', data.validCvv ? 'valid' : null);
            trigger('cvv');
          });

          payment.cvvOn('blur', () => {
            trigger('cvv');
          });

          // Tab from CVV to month input
          payment.cvvOn('tab', () => {
            monthInputRef.current?.focus();
          });

          setActivityIndicatorVisible(false);
        },
      );
    }
  }, [paymentJsScript, integrationKey, isVisible, setValue, trigger]);

  const onSubmit = (values: CardTokeniserTillValues) => {
    if (!paymentJsScript) return;

    setActivityIndicatorVisible(true);

    const data = {
      card_holder: values.cardHolder,
      month: values.month.padStart(2, '0'),
      year: values.year,
    };

    paymentJsScript.tokenize(
      data,
      (token: string) => {
        // Success callback - save card using mutation
        savePaymentCard.mutate(
          {tillTransactionToken: token},
          {
            onSuccess: () => {
              Toast.show({type: SUCCESS_CHECK});
              onClose();
            },
            onError: (error: Error) => {
              Toast.show({
                type: ERROR_MESSAGE,
                text1: error.message || 'An error occurred while saving your card',
              });
              setActivityIndicatorVisible(false);
            },
          },
        );
      },
      errors => {
        // Error callback
        showToastIfTokeniserErrors(errors);
        setActivityIndicatorVisible(false);
      },
    );
  };

  const onClose = () => {
    resetIntegrationKey();
    setPaymentJsScript(null);
    setActivityIndicatorVisible(true);
    reset();
    onCancel();
  };

  return (
    <CrossPlatformModal
      isVisible={isVisible}
      avoidKeyboard={true}
      onBackdropPress={onClose}
      onBackButtonPress={onClose}
      onModalHide={onClose}
      style={isWebDesktopScreen ? styles.webDesktopModalContent : {}}>
      <View testID="card-tokeniser-till-web-modal-content" style={modalContainerStyle}>
        {!!isActivityIndicatorVisible && <ActivityIndicator size="large" style={styles.activity} />}
        <KeyboardAwareScrollView style={styles.keyAvoidScroll} keyboardShouldPersistTaps="always">
          <View style={styles.form}>
            <View style={styles.headerRow}>
              <Title>Add new card</Title>
              <SupportedCardIcons size="small" />
            </View>

            {/* Cardholder Name */}
            <Controller
              control={control}
              name="cardHolder"
              render={({field, fieldState}) => (
                <TextInput
                  autoCapitalize="none"
                  errorMessageWhenTouched={fieldState.error?.message}
                  label="Cardholder name"
                  onBlur={field.onBlur}
                  onChangeText={field.onChange}
                  returnKeyLabel="next"
                  returnKeyType="next"
                  touched={fieldState.isTouched}
                  value={field.value}
                />
              )}
            />

            {/* Card Number (Till iframe) */}
            <Text style={styles.label}>Card number</Text>
            <View style={styles.tillFieldsWrapper}>
              <div
                tabIndex={0}
                id="number_div"
                onFocus={() => {
                  // Focus the iframe inside when the container receives focus
                  const iframe = document.querySelector('#number_div iframe') as HTMLIFrameElement;
                  iframe?.focus();
                }}
                style={{
                  height: 36,
                  width: '100%',
                  touchAction: 'manipulation',
                  cursor: 'text',
                }}
              />
            </View>
            <Text style={styles.validationError}>{formState.errors.number?.message ?? ' '}</Text>

            {/* CVV (Till iframe) */}
            <Text style={styles.label}>CVV</Text>
            <View style={styles.tillFieldsWrapper}>
              <div
                tabIndex={0}
                id="cvv_div"
                onFocus={() => {
                  // Focus the iframe inside when the container receives focus
                  const iframe = document.querySelector('#cvv_div iframe') as HTMLIFrameElement;
                  iframe?.focus();
                }}
                style={{
                  height: 36,
                  width: '100%',
                  touchAction: 'manipulation',
                  cursor: 'text',
                }}
              />
            </View>
            <Text style={styles.validationError}>{formState.errors.cvv?.message ?? ' '}</Text>

            {/* Month/Year Row */}
            <View style={styles.formRow}>
              <Controller
                control={control}
                name="month"
                render={({field, fieldState}) => (
                  <TextInput
                    ref={monthInputRef}
                    autoCapitalize="none"
                    containerStyle={styles.formRowField}
                    inputStyle={{width: '100%'}}
                    errorMessageWhenTouched={fieldState.error?.message}
                    keyboardType="number-pad"
                    label="Expiry month"
                    maxLength={2}
                    onBlur={field.onBlur}
                    onChangeText={text => field.onChange(filterNumericOnly(text))}
                    placeholder="MM"
                    returnKeyLabel="next"
                    returnKeyType="next"
                    touched={fieldState.isTouched}
                    value={field.value}
                  />
                )}
              />
              <Controller
                control={control}
                name="year"
                render={({field, fieldState}) => (
                  <TextInput
                    inputStyle={{width: '100%'}}
                    autoCapitalize="none"
                    containerStyle={styles.formRowField}
                    errorMessageWhenTouched={fieldState.error?.message}
                    keyboardType="number-pad"
                    label="Expiry year"
                    maxLength={2}
                    onBlur={field.onBlur}
                    onChangeText={text => field.onChange(filterNumericOnly(text))}
                    placeholder="YY"
                    returnKeyLabel="done"
                    returnKeyType="done"
                    touched={fieldState.isTouched}
                    value={field.value}
                  />
                )}
              />
            </View>

            {/* Buttons */}
            <View style={styles.buttonRow}>
              <Button type="outline" title="CANCEL" onPress={onClose} buttonStyle={styles.button} />
              <Button
                title="ADD CARD"
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                onPress={handleSubmit(onSubmit as any)}
                buttonStyle={styles.button}
                disabled={!formState.isValid}
              />
            </View>
          </View>
        </KeyboardAwareScrollView>
      </View>
    </CrossPlatformModal>
  );
};

export default CardTokeniserTillModal;
